import React from "react";
import PropTypes from "prop-types";

import styles from "./SectionWrapper.module.scss";

import SectionHeader from "components/elements/SectionHeader";
import LazyBackground from "components/elements/LazyBackground/LazyBackground";
import ImageGrid from "./ImageGrid/ImageGrid";
import SmartLink from "components/SmartLink";
import Button from "components/elements/Button/Button";
// import { LoggerContext } from "context/LoggerContext";

/**
 * SectionWrapper component
 * Provides consistent layout structure for all page sections with customizable alignment,
 * padding, background, and responsive behavior
 * 
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered section with appropriate layout and styling
 */
const SectionWrapper = (props) => {
	const {
		children,
		fluid = false,
		show_header = true,
		alignment = "center",
		alignment_title,
		alignment_content,
		vertical_alignment_title,
		vertical_alignment_content,
		media,
		col_width,
		col_width_title,
		col_width_content,
		className,
		bg_color,
		bg_blur,
		bg_image,
		bg_image_alt,
		text_color_title,
		contrast_schema,
		order,
		anchor,
		isFirst,
		cta_text,
		cta_link,
		 // Spacing properties
		padding_y,
		padding_x,
		margin_y,
		margin_x,
		btn_variant,
		btn_size,
		isInContainerGrid,
		isSubsection,
		//is_absolute,
	} = props;


	// Generate padding classes for vertical (Y) axis based on responsive factors
	let paddingYClasses = "";
	if (!isSubsection) {
		if (padding_y === 0) {
			paddingYClasses = "py-0";
		} else if (padding_y !== undefined) {
			const mobileYPadding = Math.max(Math.floor(padding_y * 0.6), 5);
			const tabletYPadding = Math.max(Math.floor(padding_y * 0.7), 6);
			paddingYClasses = `py-${mobileYPadding} py-md-${tabletYPadding} py-lg-${padding_y}`;
		}
	}

	// Generate padding classes for horizontal (X) axis based on responsive factors
	let paddingXClasses = "";
	if (!isSubsection) {
		if (padding_x === 0) {
			paddingXClasses = "px-0";
		} else if (padding_x !== null && padding_x !== undefined) {
			//console.log("padding_x", padding_x);
			const mobileXPadding = Math.min(Math.floor(padding_x * 0.5), 3);
			const tabletXPadding = Math.min(Math.floor(padding_x * 0.7), 4);
			paddingXClasses = `px-${mobileXPadding} px-md-${tabletXPadding} px-lg-${padding_x}`;
		} else {
			paddingXClasses = "px-3 px-sm-0";
		}
	}

	// Generate margin classes for vertical (Y) axis based on responsive factors
	let marginYClasses = "";
	if (margin_y === 0) {
		marginYClasses = "my-0";
	} else if (margin_y !== undefined) {
		const mobileYMargin = Math.max(Math.floor(margin_y * 0.7), 0);
		const tabletYMargin = Math.max(Math.floor(margin_y * 0.85), 0);
		marginYClasses = `my-${mobileYMargin} my-md-${tabletYMargin} my-lg-${margin_y}`;
	}

	// Generate margin classes for horizontal (X) axis based on responsive factors
	let marginXClasses = "";
	if (margin_x === 0) {
		marginXClasses = "mx-0";
	} else if (margin_x !== undefined) {
		const mobileXMargin = Math.min(Math.floor(margin_x * 0.7), 3);
		const tabletXMargin = Math.min(Math.floor(margin_x * 0.85), 4);
		marginXClasses = `mx-${mobileXMargin} mx-md-${tabletXMargin} mx-lg-${margin_x}`;
	}
	
	// Prepare class modifiers for layout
	const section_alignment = alignment ? `justify-content-${alignment}` : "";
	const title_vertical_alignment = vertical_alignment_title ? `align-self-${vertical_alignment_title}` : "";
	const content_vertical_alignment = vertical_alignment_content ? `align-self-${vertical_alignment_content}` : "";
	const section_col_width = !isInContainerGrid && col_width ? "col-lg-" + col_width : "";
	const content_col_width = col_width_content ? "col-lg-" + col_width_content : "";
	const title_col_width = col_width_title ? "col-lg-" + col_width_title : "";
	const section_order = order === "backwards" ? "flex-row-reverse flex-wrap-reverse" : "";

	/**
	 * Determine Bootstrap alignment class based on alignment prop
	 * @param {string} alignment - The alignment value (start, center, or end)
	 * @returns {string} - The corresponding Bootstrap margin utility class
	 */
	const getAlignmentClass = (alignment) => {
		switch (alignment) {
			case "start":
				return "me-auto";
			case "center":
				return "mx-auto";
			case "end":
				return "ms-auto";
			default:
				return "";
		}
	};

	const title_alignment_class = getAlignmentClass(alignment_title);
	const content_alignment_class = getAlignmentClass(alignment_content);
	
	return (
		<section
			id={anchor}
			className={`${styles.SectionWrapper} d-flex position-relative overflow-hidden z-1 ${className || ""} ${marginYClasses} ${marginXClasses} ${section_col_width} mx-auto`}
			style={{ background: bg_color || "transparent", color: text_color_title || (bg_image && "#ffffff") || "" }}
		>
			{bg_image && (
				<LazyBackground
					src={bg_image}
					alt={bg_image_alt || ""}
					className={`bg-image ${bg_blur && "blur-overlay"}`}
					isFirst={isFirst}
				/>
			)}

			<div className={`${fluid ? `container-fluid p-0` : `container g-0 ${paddingYClasses} ${paddingXClasses}`} ${contrast_schema || ""}`}>
				<div className={`row g-0 h-100 ${section_alignment}`}>
					<div className={`col-12`}>
						<div className={`row g-0 h-100 ${section_alignment} ${section_order}`}>
							{/* Title Area */}
							{show_header && (
								<div className={`col-12 ${title_col_width} ${title_alignment_class} ${title_vertical_alignment}`}>
									<SectionHeader
										{...props}
									/>
									{cta_text && cta_link && (
										<SmartLink to={cta_link}>
											<Button 
												size={btn_size || "md"} 
												variant={btn_variant || "primary"} 
												letterCase="uppercase"
											>
												{cta_text}
											</Button>
										</SmartLink>
									)}
								</div>
							)}

							{/* Content Area */}
							<div className={`col-12 ${content_col_width} ${content_alignment_class} ${content_vertical_alignment}`}>
								<div className="row gx-5">
									{media && media.length > 0 && (
										<div className={`col-12 col-lg`}>
											<ImageGrid
												images={media}
												className=""
											/>
										</div>
									)}
									{/* Content */}
									{children && <div className={`col-12 col-lg`}>{children}</div>}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

SectionWrapper.propTypes = {
	children: PropTypes.node,
	col_width: PropTypes.number,
	className: PropTypes.string,
	fluid: PropTypes.bool,
	alignment: PropTypes.oneOf(["start", "center", "end"]),
	title: PropTypes.string,
	short_desc: PropTypes.string,
	desc: PropTypes.object,
	image: PropTypes.string,
	image_alt: PropTypes.string,
	bg_image: PropTypes.string,
	bg_image_alt: PropTypes.string,
	medias: PropTypes.arrayOf(
		PropTypes.shape({
			mediaUrl: PropTypes.string.isRequired,
			alt_text: PropTypes.string,
		})
	),
	imagePosition: PropTypes.oneOf(["title", "content", "both", "none"]),
	padding_y: PropTypes.oneOf([0, 1, 2, 3, 4, 5]),
	padding_x: PropTypes.oneOf([0, 1, 2, 3, 4, 5]),
	margin_y: PropTypes.oneOf([0, 1, 2, 3, 4, 5]),
	margin_x: PropTypes.oneOf([0, 1, 2, 3, 4, 5]),
	// Custom section header prop
	customSectionHeader: PropTypes.node
};

export default SectionWrapper;
